import { useMachine } from '@xstate/react';
import { useCallback, useEffect, useState } from 'react';
import {
  NavigateFunction,
  useLocation,
  useNavigate as useNavigateImport,
} from 'react-router';
import {
  SetURLSearchParams,
  URLSearchParamsInit,
  useSearchParams as useSearchParamsImport,
} from 'react-router-dom';
import { userService as userServiceImport } from '../../apiServices/UserService';
import { useLocale } from '../Locale/useLocale';
import {
  matchPath,
  REDIRECT_AUTH_ROUTES,
  UNAUTHENTICATED_ROUTES,
} from '../../helpers/routing';
import { defaultUser } from '../../stubs';
import { AuthenticationContext, machine } from './AuthenticationMachine';
import { UserContext } from './useUser';

export type UserContextProps = {
  children: React.ReactNode;
  useSearchParams?: (
    defaultInit?: URLSearchParamsInit
  ) => [URLSearchParams, SetURLSearchParams];
  userService?: typeof userServiceImport;
  useNavigate?: () => NavigateFunction;
  initialContext?: Partial<AuthenticationContext>;
  initialState?: string;
};

export const UserContextProvider = ({
  children,
  useSearchParams = useSearchParamsImport,
  userService = userServiceImport,
  useNavigate = useNavigateImport,
  initialContext = {},
  initialState = 'INITIAL',
}: UserContextProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const magicToken = searchParams.get('magicToken') || undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [authMachine] = useState(
    machine({ userService, initialContext, initialState })
  );
  const [machineState, send] = useMachine(authMachine, {
    input: { magicToken },
  });

  const userData = machineState.context.userData || defaultUser;
  const isAuthenticated = machineState.context.isAuthenticated;
  const { setLocale } = useLocale();

  useEffect(() => {
    if (machineState.context.userData?.locale) {
      setLocale(machineState.context.userData.locale);
    }
  }, [machineState.context.userData?.locale, setLocale]);

  useEffect(() => {
    if (searchParams.has('magicToken')) {
      searchParams.delete('magicToken');
      setSearchParams(searchParams);
    }
  }, [magicToken, searchParams, setSearchParams]);

  const fetchUser = useCallback(
    () => send({ type: 'POLL_IS_AUTHENTICATED' }),
    [send]
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        fetchUser();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, fetchUser]);

  const setIsAuthenticated = useCallback(() => {
    send({ type: 'SIGN_UP' });
  }, [send]);

  const signOut = useCallback(() => send({ type: 'SIGN_OUT' }), [send]);

  useEffect(() => {
    if (machineState.context.isAuthenticated) {
      const shouldRedirect: boolean = matchPath(
        currentPath,
        REDIRECT_AUTH_ROUTES
      );
      if (shouldRedirect) navigate('/');
    }

    if (machineState.matches('UI_SIGN_IN')) {
      const shouldRedirect: boolean = !matchPath(
        currentPath,
        UNAUTHENTICATED_ROUTES
      );
      if (shouldRedirect) navigate('/sign-in');
    }
  }, [machineState, currentPath, navigate]);

  return (
    <UserContext.Provider
      value={{
        ...userData,
        isAuthenticated,
        setIsAuthenticated,
        signOut,
        fetchUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
